import { BellRing, X } from 'lucide-react-native';
import { Modal, Pressable, StyleSheet, Text, View } from 'react-native';

import { Button } from '@/src/components/Button';
import { useToast } from '@/src/components/Toast';
import { useAppStore } from '@/src/state/app-store';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

import { useWebPush } from './use-web-push';

const snoozeMs = 7 * 24 * 60 * 60 * 1000;

function futureIso(msFromNow: number) {
  return new Date(Date.now() + msFromNow).toISOString();
}

function isSnoozed(value: string | null) {
  if (!value) {
    return false;
  }

  const time = new Date(value).getTime();

  return Number.isFinite(time) && time > Date.now();
}

type PwaFirstRunPushPromptProps = {
  enabled?: boolean;
};

export function PwaFirstRunPushPrompt({
  enabled = true,
}: PwaFirstRunPushPromptProps) {
  const push = useWebPush();
  const { showError, showSuccess } = useToast();
  const webPushPromptSnoozedUntil = useAppStore(
    (state) => state.webPushPromptSnoozedUntil,
  );
  const setWebPushPromptSnoozedUntil = useAppStore(
    (state) => state.setWebPushPromptSnoozedUntil,
  );
  const visible =
    enabled &&
    push.supported &&
    push.isStandalone &&
    push.permission === 'default' &&
    !push.enabledOnThisDevice &&
    !isSnoozed(webPushPromptSnoozedUntil);

  function snoozePrompt() {
    setWebPushPromptSnoozedUntil(futureIso(snoozeMs));
  }

  async function handleEnable() {
    try {
      await push.enable();
      setWebPushPromptSnoozedUntil(null);
      showSuccess('Push notifications are enabled on this device.');
    } catch (error) {
      showError(error, 'Unable to enable push notifications.');
    }
  }

  return (
    <Modal
      animationType="fade"
      transparent
      visible={visible}
      onRequestClose={snoozePrompt}
    >
      <Pressable style={styles.backdrop} onPress={snoozePrompt}>
        <Pressable
          style={styles.card}
          onPress={(event) => event.stopPropagation()}
        >
          <Pressable
            accessibilityLabel="Not now"
            accessibilityRole="button"
            style={styles.closeButton}
            onPress={snoozePrompt}
          >
            <X color={colors.mutedText} size={20} strokeWidth={2.5} />
          </Pressable>
          <View style={styles.iconShell}>
            <BellRing color={colors.primary} size={30} strokeWidth={2.5} />
          </View>
          <Text style={styles.title}>Turn on Frienzzi notifications?</Text>
          <Text style={styles.body}>
            Get browser notifications for comments, mentions, follows,
            reactions, and Frienzzi updates from this installed app.
          </Text>
          <View style={styles.actions}>
            <Button
              title="Enable notifications"
              loading={push.busy}
              onPress={handleEnable}
            />
            <Button
              title="Not now"
              variant="secondary"
              disabled={push.busy}
              onPress={snoozePrompt}
            />
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  actions: {
    gap: spacing.sm,
    width: '100%',
  },
  backdrop: {
    alignItems: 'center',
    backgroundColor: 'rgba(26, 12, 62, 0.28)',
    flex: 1,
    justifyContent: 'center',
    padding: spacing.xl,
  },
  body: {
    color: colors.mutedText,
    fontSize: 15,
    lineHeight: 22,
    textAlign: 'center',
  },
  card: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 16,
    borderWidth: 1,
    gap: spacing.md,
    maxWidth: 420,
    padding: spacing.xl,
    position: 'relative',
    width: '100%',
  },
  closeButton: {
    padding: spacing.xs,
    position: 'absolute',
    right: spacing.md,
    top: spacing.md,
  },
  iconShell: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 999,
    borderWidth: 1,
    height: 64,
    justifyContent: 'center',
    width: 64,
  },
  title: {
    color: colors.text,
    fontSize: 24,
    fontWeight: '900',
    textAlign: 'center',
  },
});
